import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Camera, Save, Key } from 'lucide-react';
import ImageCropper from '../components/ImageCropper';

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', phone: '', username: '' });
  const [pwForm, setPwForm] = useState({ new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (user) setForm({ name: user.name||'', email: user.email||'', phone: user.phone||'', username: user.username||'' });
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/auth/profile', form);
      await refreshUser();
      setMsg('Profile updated successfully');
      setTimeout(() => setMsg(''), 3000);
    } catch(e) { setMsg('Error saving profile'); }
    setSaving(false);
  };

  const changePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm) { setMsg('Passwords do not match'); return; }
    if (pwForm.new_password.length < 6) { setMsg('Password must be at least 6 characters'); return; }
    try {
      await api.put('/api/auth/profile/password', { new_password: pwForm.new_password });
      setPwForm({ new_password: '', confirm: '' });
      setMsg('Password changed successfully');
      setTimeout(() => setMsg(''), 3000);
    } catch(e) { setMsg('Error changing password'); }
  };

  const [picUrl, setPicUrl] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);

  const uploadPicture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCropSrc(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleCropped = async (blob) => {
    setCropSrc(null);
    const formData = new FormData();
    formData.append('file', new File([blob], 'profile.jpg', {type:'image/jpeg'}));
    try {
      const res = await api.post('/api/auth/profile/picture', formData, { headers: { 'Content-Type': 'multipart/form-data' }});
      setPicUrl(res.data.url + '?t=' + Date.now());
      await refreshUser();
      setMsg('Profile picture updated!');
      setTimeout(() => setMsg(''), 3000);
    } catch(e) { setMsg('Error uploading picture'); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {cropSrc && <ImageCropper src={cropSrc} shape="circle" onCrop={handleCropped} onCancel={() => setCropSrc(null)} />}
      <div>
        <h3 className="text-xl font-bold text-white">My Profile</h3>
        <p className="text-dark-400 text-sm">Manage your account details and password</p>
      </div>

      {msg && <div className={`p-3 rounded-lg text-sm ${msg.includes('Error') || msg.includes('match') ? 'bg-red-500/10 text-red-400' : 'bg-green-500/10 text-green-400'}`}>{msg}</div>}

      {/* Profile Picture */}
      <div className="glass rounded-xl p-5">
        <h4 className="text-white font-medium mb-4">Profile Picture</h4>
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-primary-500/20 flex items-center justify-center overflow-hidden border-2 border-dark-600">
              {(picUrl || user?.profile_picture) ? (
                <img src={picUrl || user.profile_picture} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-bold text-primary-400">{user?.name?.charAt(0)}</span>
              )}
            </div>
          </div>
          <div>
            <input type="file" accept="image/*" id="pic-upload" className="hidden" onChange={uploadPicture} />
            <label htmlFor="pic-upload" className="cursor-pointer flex items-center gap-2 px-4 py-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded-lg text-sm border border-blue-500/20">
              <Camera className="w-4 h-4" /> Upload Photo
            </label>
            <p className="text-dark-500 text-xs mt-1">JPG, PNG supported</p>
          </div>
        </div>
      </div>

      {/* Profile Details */}
      <div className="glass rounded-xl p-5 space-y-4">
        <h4 className="text-white font-medium">Account Details</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[['Name','name'],['Username','username'],['Email','email'],['Phone','phone']].map(([label, key]) => (
            <div key={key}>
              <label className="block text-sm text-dark-300 mb-1">{label}</label>
              <input value={form[key]} onChange={e => setForm({...form, [key]: e.target.value})}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500" />
            </div>
          ))}
        </div>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-primary-500 hover:bg-primary-600 text-dark-900 font-medium rounded-lg text-sm">
          <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Change Password */}
      <div className="glass rounded-xl p-5 space-y-4">
        <h4 className="text-white font-medium flex items-center gap-2"><Key className="w-4 h-4" />Change Password</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-dark-300 mb-1">New Password</label>
            <input type="password" value={pwForm.new_password} onChange={e => setPwForm({...pwForm, new_password: e.target.value})}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500" />
          </div>
          <div>
            <label className="block text-sm text-dark-300 mb-1">Confirm Password</label>
            <input type="password" value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500" />
          </div>
        </div>
        <button onClick={changePassword} className="flex items-center gap-2 px-6 py-2 bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 border border-orange-500/20 rounded-lg text-sm">
          <Key className="w-4 h-4" />Change Password
        </button>
      </div>
    </div>
  );
}
